import {
  BALL_RADIUS,
  COURT_HEIGHT,
  COURT_WIDTH,
  FLOOR,
  HOOP,
  START,
  predict,
  type GameState,
  type Point,
} from "./hoop-game";

/**
 * Draws one frame of the game. It decides nothing: everything it shows is read
 * from `GameState`, and every colour is read from the design tokens at draw
 * time — never a literal — so the court follows the light and dark themes and
 * `pnpm tokens:check` has nothing to object to.
 */

export interface Palette {
  ground: string;
  floor: string;
  board: string;
  ink: string;
  muted: string;
  accent: string;
  warm: string;
  good: string;
}

/** Read the tokens off an element, so a theme change is picked up on the next frame. */
export function readPalette(el: Element): Palette {
  const style = getComputedStyle(el);
  const token = (name: string): string => style.getPropertyValue(name).trim();
  return {
    ground: token("--c-surface"),
    floor: token("--c-surface2"),
    board: token("--c-bg"),
    ink: token("--c-text"),
    muted: token("--c-muted"),
    accent: token("--c-accent"),
    warm: token("--c-warm"),
    good: token("--c-good"),
  };
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** The file: a rounded tile showing the logo, or the accent colour until it loads. */
function drawFile(
  ctx: CanvasRenderingContext2D,
  p: Palette,
  logo: HTMLImageElement | null,
  at: Point,
): void {
  const size = BALL_RADIUS * 2;
  ctx.save();
  roundedRect(ctx, at.x - size / 2, at.y - size / 2, size, size, 8);
  ctx.clip();
  ctx.fillStyle = p.accent;
  ctx.fillRect(at.x - size / 2, at.y - size / 2, size, size);
  if (logo !== null && logo.complete && logo.naturalWidth > 0) {
    ctx.drawImage(logo, at.x - size / 2, at.y - size / 2, size, size);
  }
  ctx.restore();

  roundedRect(ctx, at.x - size / 2, at.y - size / 2, size, size, 8);
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = p.ink;
  ctx.stroke();
}

/**
 * The figure: ink for the limbs and head, the accent for the shirt, so it reads
 * on both themes. Original line art — nothing is borrowed from anywhere.
 */
function drawStick(ctx: CanvasRenderingContext2D, p: Palette, g: GameState): void {
  const s = g.stick;
  if (s === null) return;

  const { x, y } = s;
  const lean = s.pose === "run" ? 4 : 0;
  const neck = { x: x + lean, y: y - 22 };
  const shoulder = { x: neck.x, y: neck.y + 3 };
  const swing = Math.sin(s.t * 0.5);

  let kneeL: Point;
  let footL: Point;
  let kneeR: Point;
  let footR: Point;
  let handL: Point;
  let handR: Point;

  if (s.pose === "run") {
    footL = { x: x + 13 * swing, y: y + 24 - Math.max(0, -swing) * 7 };
    kneeL = { x: x + 7 * swing + 4, y: y + 12 };
    footR = { x: x - 13 * swing, y: y + 24 - Math.max(0, swing) * 7 };
    kneeR = { x: x - 7 * swing + 4, y: y + 12 };
    handL = { x: x + 11 * swing + lean, y: neck.y + 17 };
    handR = { x: x - 11 * swing + lean, y: neck.y + 17 };
  } else if (s.pose === "grab") {
    footL = { x: x - 9, y: y + 24 };
    kneeL = { x: x - 12, y: y + 12 };
    footR = { x: x + 9, y: y + 24 };
    kneeR = { x: x + 12, y: y + 12 };
    handL = { x: g.ball.x - 12, y: g.ball.y + 6 };
    handR = { x: g.ball.x + 12, y: g.ball.y + 6 };
  } else if (s.pose === "stand") {
    footL = { x: x - 6, y: y + 24 };
    kneeL = { x: x - 6, y: y + 12 };
    footR = { x: x + 6, y: y + 24 };
    kneeR = { x: x + 6, y: y + 12 };
    handL = { x: x - 12, y: y - 2 };
    handR = { x: x + 12, y: y - 2 };
  } else {
    // Jump and dunk: knees tucked, both hands on the file.
    footL = { x: x - 9, y: y + 22 };
    kneeL = { x: x - 13, y: y + 12 };
    footR = { x: x + 11, y: y + 17 };
    kneeR = { x: x + 15, y: y + 9 };
    handL = { x: g.ball.x - 12, y: g.ball.y + 6 };
    handR = { x: g.ball.x + 12, y: g.ball.y + 6 };
  }

  const line = (points: Point[], colour: string, width: number): void => {
    const [first, ...rest] = points;
    if (first === undefined) return;
    ctx.strokeStyle = colour;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    for (const point of rest) ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };
  const elbow = (from: Point, to: Point, side: number): Point => ({
    x: (from.x + to.x) / 2 + side * 5,
    y: (from.y + to.y) / 2 + 3,
  });

  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  line([{ x, y }, kneeL, footL], p.ink, 3);
  line([{ x, y }, kneeR, footR], p.ink, 3);
  line([shoulder, elbow(shoulder, handL, -1), handL], p.ink, 3);
  line([shoulder, elbow(shoulder, handR, 1), handR], p.ink, 3);
  line([{ x, y }, neck], p.accent, 5);

  ctx.beginPath();
  ctx.arc(neck.x, neck.y - 8, 6.5, 0, Math.PI * 2);
  ctx.fillStyle = p.board;
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = p.ink;
  ctx.stroke();
}

/** One frame. The canvas is expected to be scaled so that one unit is one court unit. */
export function drawGame(
  ctx: CanvasRenderingContext2D,
  p: Palette,
  g: GameState,
  logo: HTMLImageElement | null,
): void {
  ctx.clearRect(0, 0, COURT_WIDTH, COURT_HEIGHT);
  ctx.fillStyle = p.ground;
  ctx.fillRect(0, 0, COURT_WIDTH, COURT_HEIGHT);
  ctx.fillStyle = p.floor;
  ctx.fillRect(0, FLOOR, COURT_WIDTH, COURT_HEIGHT - FLOOR);

  // Backboard, facing the player.
  ctx.fillStyle = p.board;
  ctx.strokeStyle = p.ink;
  ctx.lineWidth = 3;
  roundedRect(ctx, HOOP.x - 64, HOOP.rimY - 96, 128, 88, 6);
  ctx.fill();
  ctx.stroke();
  ctx.lineWidth = 2;
  roundedRect(ctx, HOOP.x - 24, HOOP.rimY - 44, 48, 32, 3);
  ctx.stroke();

  // The back half of the rim, behind the file.
  ctx.lineCap = "round";
  ctx.strokeStyle = p.warm;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.ellipse(HOOP.x, HOOP.rimY, HOOP.rx, HOOP.ry, 0, Math.PI, Math.PI * 2);
  ctx.stroke();

  // The aim line: the whole arc, green when this shot goes in.
  if (g.drag !== null) {
    const { points, goesIn } = predict(g.drag);
    ctx.fillStyle = goesIn ? p.good : p.muted;
    points.forEach((point, i) => {
      if (i % 3 !== 2) return;
      ctx.beginPath();
      ctx.arc(point.x, point.y, 3, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.strokeStyle = p.muted;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(START.x, START.y);
    ctx.lineTo(g.drag.x, g.drag.y);
    ctx.stroke();
    drawFile(ctx, p, logo, g.drag);
  } else {
    drawFile(ctx, p, logo, g.ball);
  }

  drawStick(ctx, p, g);

  // The net and the front half of the rim go over the file, so a file that has
  // gone through sits inside the net.
  const sway = g.netSway > 0 ? Math.sin(g.frame / 2) * g.netSway : 0;
  ctx.strokeStyle = p.muted;
  ctx.lineWidth = 1.5;
  ctx.globalAlpha = 0.85;
  for (let i = 0; i <= 6; i++) {
    const a = (Math.PI * i) / 6;
    ctx.beginPath();
    ctx.moveTo(HOOP.x + HOOP.rx * Math.cos(a), HOOP.rimY + HOOP.ry * Math.sin(a));
    ctx.lineTo(HOOP.x + HOOP.rx * 0.5 * Math.cos(a) + sway, HOOP.rimY + 52 + 4 * Math.sin(a));
    ctx.stroke();
  }
  for (let j = 1; j <= 2; j++) {
    ctx.beginPath();
    ctx.ellipse(
      HOOP.x + (sway * j) / 3,
      HOOP.rimY + 18 * j,
      HOOP.rx - 14 * j,
      HOOP.ry - 2 * j,
      0,
      0,
      Math.PI,
    );
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.strokeStyle = p.warm;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.ellipse(HOOP.x, HOOP.rimY, HOOP.rx, HOOP.ry, 0, 0, Math.PI);
  ctx.stroke();

  if (g.scored) {
    ctx.fillStyle = p.good;
    ctx.font = "600 22px Inter, system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Swish!", 58, HOOP.rimY + 4);
  }
}
